import { gapi } from 'gapi-script';

// Helper to safely access gapi
const safeGapi = () => {
  if (typeof window !== 'undefined' && window.gapi) {
    return window.gapi;
  }
  return gapi;
};

let isInitialized = false;

export const initGoogleClient = (clientId, apiKey) => {
  return new Promise((resolve, reject) => {
    const g = safeGapi();
    
    if (!g) {
      reject(new Error("FATAL: Google API Client script not loaded (window.gapi missing). Check your internet connection or ad-blocker."));
      return;
    }

    if (isInitialized) {
      resolve(g);
      return;
    }

    g.load('client:auth2', async () => {
      try {
        await g.client.init({
          apiKey: apiKey,
          clientId: clientId,
          discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest"],
          scope: "https://www.googleapis.com/auth/calendar.readonly",
          plugin_name: "FamilyPlannerApp"
        });
        isInitialized = true;
        resolve(g);
      } catch (err) {
        console.error("Google Init Error Raw:", err);
        const errorMsg = err.details?.message || err.result?.error?.message || err.message || JSON.stringify(err);
        const enhancedError = new Error(errorMsg);
        enhancedError.originalError = err;
        reject(enhancedError);
      }
    });
  });
};

export const signInToGoogle = async () => {
  const g = safeGapi();
  if (!g || !g.auth2) throw new Error("Google Client not initialized. Call initGoogleClient first.");
  
  const authInstance = g.auth2.getAuthInstance();
  
  if (!authInstance.isSignedIn.get()) {
    try {
        await authInstance.signIn();
    } catch (e) {
        if (e.error === 'server_error') {
            throw new Error(`Google Server Error. Check "Authorized JavaScript origins" in Google Cloud Console.`);
        }
        throw new Error(`Sign In Failed: ${e.error || e.message}`);
    }
  }
  
  return authInstance.currentUser.get();
};

export const signOutFromGoogle = async () => {
  const g = safeGapi();
  if (!g || !g.auth2) return;
  await g.auth2.getAuthInstance().signOut();
};

export const getGoogleAuthState = () => {
    const g = safeGapi();
    if (!g || !g.auth2) return { isSignedIn: false, userEmail: null };
    const auth = g.auth2.getAuthInstance();
    const isSignedIn = auth.isSignedIn.get();
    let userEmail = null;
    if (isSignedIn) {
        try {
            userEmail = auth.currentUser.get().getBasicProfile().getEmail();
        } catch (e) { /* ignore */ }
    }
    return { isSignedIn, userEmail };
};

export const listGoogleCalendars = async () => {
  try {
    const g = safeGapi();
    const response = await g.client.calendar.calendarList.list();
    return response.result.items.map(cal => ({
      id: cal.id,
      name: cal.summary,
      color: cal.backgroundColor,
      primary: cal.primary || false,
      accessRole: cal.accessRole, // owner, reader, etc.
      source: 'google'
    }));
  } catch (error) {
    console.error("Error fetching Google Calendars", error);
    throw new Error("Failed to fetch calendars: " + (error.result?.error?.message || error.message));
  }
};

export const listGoogleEvents = async (calendarId, timeMin, timeMax) => {
  try {
    const g = safeGapi();
    // Double check auth before call
    if (!g.auth2.getAuthInstance().isSignedIn.get()) {
        console.warn("Skipping Google Event fetch: Not signed in.");
        return [];
    }

    const response = await g.client.calendar.events.list({
      'calendarId': calendarId,
      'timeMin': timeMin || (new Date()).toISOString(),
      'timeMax': timeMax,
      'showDeleted': false,
      'singleEvents': true,
      'maxResults': 250,
      'orderBy': 'startTime'
    });

    return response.result.items.map(item => ({
      id: item.id,
      title: item.summary || 'Kein Titel',
      description: item.description,
      start: item.start.dateTime || item.start.date, 
      end: item.end.dateTime || item.end.date,
      allDay: !item.start.dateTime,
      location: item.location,
      source: 'google',
      calendarId: calendarId,
      originalEvent: item
    }));
  } catch (error) {
    console.error(`Error fetching events for calendar ${calendarId}`, error);
    return [];
  }
};